/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

/* eslint-disable no-invalid-this, max-len */

'use strict';

// MODULES //

import IS_LITTLE_ENDIAN from '@stdlib/assert-is-little-endian';
import { isPrimitive as isNumber } from '@stdlib/assert-is-number';
import { isPrimitive as isBoolean } from '@stdlib/assert-is-boolean';
import { isPrimitive as isBigInt } from '@stdlib/assert-is-bigint';
import isComplexLike from '@stdlib/assert-is-complex-like';
import isAllowedCast from '@stdlib/ndarray-base-assert-is-allowed-data-type-cast';
import complexDType from '@stdlib/complex-dtype';
import format from '@stdlib/error-tools-fmtprodmsg';
import boolean2number from './boolean2number.js';
import bigint2number from './bigint2number.js';
import PRIVATE_BUFFER from './private_buffer.js';


// MAIN //

/**
* Returns a function for writing field data.
*
* @private
* @param {Object} obj - field object
* @param {string} method - data view method name
* @returns {Function} function for writing field data
*/
function setComplex( obj, method ) {
	return setter;

	/**
	* Writes a complex number to an underlying byte buffer.
	*
	* @private
	* @param {*} value - value to set
	* @throws {TypeError} cannot cast provided values to field data type
	*/
	function setter( value ) {
		var view;
		var dt;
		var re;
		var im;
		if ( isComplexLike( value ) ) {
			dt = complexDType( value ) || obj.type;
			re = value.re;
			im = value.im;
		} else if ( isNumber( value ) ) {
			dt = ( obj.type === 'complex64' ) ? 'float32' : 'float64';
			re = value;
			im = 0.0;
		} else if ( isBigInt( value ) ) {
			dt = 'int64'; // note: the specific integer data type (int64 vs uint64) should not matter here
			re = bigint2number( value );
			im = 0.0;
		} else if ( isBoolean( value ) ) {
			dt = 'bool';
			re = boolean2number( value );
			im = 0.0;
		} else {
			dt = 'generic';
			re = value;
			im = 0.0;
		}
		if ( !isAllowedCast( dt, obj.type, obj.castingMode ) ) {
			throw new TypeError( format( '2KJIY', obj.name, obj.type, dt ) );
		}
		view = this[ PRIVATE_BUFFER ];
		view[ method ]( obj.byteOffset, re, IS_LITTLE_ENDIAN );
		view[ method ]( obj.byteOffset+(obj.byteLength/2), im, IS_LITTLE_ENDIAN );
	}
}


// EXPORTS //

export default setComplex;
